/**
 * Утилита для маппинга ошибок бекенда на понятные сообщения пользователю
 * 
 * Соответствует errorMessages из бекенда:
 * - BAD_DATA
 * - EMAIL_EXIST
 * - PHONE_EXIST
 * - WRONG_PASSWORD
 * - USER_NOT_FOUND
 * - INVALID_TOKEN
 * - SERVER_ERROR
 */

/**
 * Карта соответствия кодов ошибок бекенда ключам перевода
 */
const ERROR_KEY_MAP = {
  // Ошибки авторизации
  'WRONG_PASSWORD': 'auth.login.errors.wrongPassword',
  'USER_NOT_FOUND': 'auth.login.errors.userNotFound',
  'INVALID_TOKEN': 'auth.errors.invalidToken',
  'MISSING_AUTHORIZATION_HEADER': 'auth.errors.missingAuthHeader',
  
  // Ошибки регистрации
  'EMAIL_EXIST': 'auth.register.errors.emailExist',
  'PHONE_EXIST': 'auth.register.errors.phoneExist',
  'BAD_DATA': 'auth.errors.badData',
  
  // Общие ошибки
  'SERVER_ERROR': 'common.errors.serverError',
  'CONFIRM_CODE_IS_WRONG': 'auth.errors.wrongCode',
  'CONFIRM_CODE_EXPIRED': 'auth.errors.codeExpired',
  'USER_BANNED': 'auth.errors.userBanned',
};

/**
 * Получает ключ перевода для ошибки бекенда
 * @param {string} backendErrorCode - Код ошибки от бекенда
 * @returns {string} - Ключ для i18n
 */
export const getErrorKey = (backendErrorCode) => {
  return ERROR_KEY_MAP[backendErrorCode] || 'common.errors.unknown';
};

/**
 * Извлекает сообщение об ошибке из ответа сервера
 * @param {Error} error - Объект ошибки от axios
 * @returns {string} - Ключ перевода для ошибки
 */
export const getBackendErrorKey = (error) => {
  // Проверяем наличие ответа от сервера
  if (!error.response) {
    // Ошибка сети или другая клиентская ошибка
    return 'common.errors.networkError';
  }

  const { status, data } = error.response;
  
  // Если в data есть message - это может быть код ошибки бекенда
  if (data?.message) {
    const errorKey = getErrorKey(data.message);
    if (errorKey !== 'common.errors.unknown') {
      return errorKey;
    }
  }

  // Маппинг по HTTP статусу
  switch (status) {
    case 400:
      return 'common.errors.badRequest';
    case 401:
      return 'auth.errors.unauthorized';
    case 403:
      return 'auth.errors.forbidden';
    case 404:
      return 'common.errors.notFound';
    case 409:
      return 'common.errors.conflict';
    case 422:
      return 'common.errors.validationError';
    case 429:
      return 'common.errors.tooManyRequests';
    case 500:
    case 502:
    case 503:
      return 'common.errors.serverError';
    default:
      return 'common.errors.unknown';
  }
};

/**
 * Проверяет, является ли ошибкой "Email уже существует"
 * @param {Error} error - Объект ошибки
 * @returns {boolean}
 */
export const isEmailExistError = (error) => {
  return error.response?.data?.message === 'EMAIL_EXIST';
};

/**
 * Проверяет, является ли ошибкой "Неверный пароль"
 * @param {Error} error - Объект ошибки
 * @returns {boolean}
 */
export const isWrongPasswordError = (error) => {
  return error.response?.data?.message === 'WRONG_PASSWORD';
};

/**
 * Проверяет, является ли ошибкой "Пользователь не найден"
 * @param {Error} error - Объект ошибки
 * @returns {boolean}
 */
export const isUserNotFoundError = (error) => {
  return error.response?.data?.message === 'USER_NOT_FOUND';
};

/**
 * Проверяет, является ли ошибкой "Некорректные данные"
 * @param {Error} error - Объект ошибки
 * @returns {boolean}
 */
export const isBadDataError = (error) => {
  return error.response?.data?.message === 'BAD_DATA';
};

export default {
  getErrorKey,
  getBackendErrorKey,
  isEmailExistError,
  isWrongPasswordError,
  isUserNotFoundError,
  isBadDataError,
};
